import { useMemo } from 'react';
import { AssocArray } from 'tools/types';
import {
    ComponentSettingsKpiChartMeasureField,
    DisplayMask,
    IKpiChartVarianceSettings,
    IKpiResults,
    IKpiVarianceResult,
    formatValueFull,
    getKpiChartMeasureFieldDisplayLabel,
    getKpiChartVariancePeriodLabel,
} from '@report-content';
import { ChartMetricsInfoType } from 'components/metric/MetricViewer';
import useBundleTranslation from 'i18n';

interface UseKpiChartSummaryStatsParams {
    data: Array<AssocArray<string>>;
    kpiData: Array<IKpiResults>;
    measureFields: Array<ComponentSettingsKpiChartMeasureField>;
    xAxisField: string;
    kpiVariance: IKpiChartVarianceSettings;
    kpiVarianceResult?: IKpiVarianceResult | null;
    displayMasks: Array<DisplayMask>;
    elementId: number;
}

function getFormattedValue(row: AssocArray<string>, field: string): string {
    return row[field + '-formatted'] ?? row[field];
}

export function useKpiChartSummaryStats({
    data,
    kpiData,
    measureFields,
    xAxisField,
    kpiVariance,
    kpiVarianceResult,
    displayMasks,
    elementId,
}: UseKpiChartSummaryStatsParams): ChartMetricsInfoType | null {
    const { t } = useBundleTranslation(['components/report-content']);

    return useMemo<ChartMetricsInfoType | null>(() => {
        const primary = measureFields.find((f) => f.is_primary == 'Y') ?? measureFields[0];
        if (!primary || !data.length) {
            return null;
        }
        const valid = data.filter((row) => !isNaN(parseFloat(row[primary.reference_name])));
        if (!valid.length) {
            return null;
        }
        const last = valid[valid.length - 1];
        const dm = displayMasks.find((m) => m.display_mask_id == primary.display_mask_id);

        let variance: number | null = null;
        let varianceMode: 'percentage' | 'absolute' | undefined;
        let varianceFormatted: string | undefined;
        let varianceTargetValueFormatted: string | undefined;
        if (kpiVariance.includeKpiVariance == 'Y' && kpiVarianceResult) {
            variance = kpiVarianceResult.variance;
            varianceMode = kpiVarianceResult.variance_mode;
            if (varianceMode === 'absolute') {
                varianceFormatted = kpiVarianceResult.variance_formatted
                    ? kpiVarianceResult.variance_formatted
                    : //@ts-ignore
                      formatValueFull(Math.abs(variance), dm);
            }
            if (kpiVarianceResult.target_value != null) {
                varianceTargetValueFormatted =
                    //@ts-ignore
                    kpiVarianceResult.target_value_formatted ?? formatValueFull(kpiVarianceResult.target_value, dm);
            }
        }

        const additionalMeasureValues = measureFields
            .filter((field) => field !== primary)
            .map((field) => {
                const fieldValid = data.filter((row) => !isNaN(parseFloat(row[field.reference_name])));
                if (!fieldValid.length) {
                    return null;
                }
                return {
                    label: getKpiChartMeasureFieldDisplayLabel(field),
                    value: getFormattedValue(fieldValid[fieldValid.length - 1], field.reference_name),
                };
            })
            .filter((v): v is { label: string; value: string } => v !== null);

        let minRow = valid[0];
        let maxRow = valid[0];
        valid.forEach((row) => {
            const v = parseFloat(row[primary.reference_name]);
            if (v < parseFloat(minRow[primary.reference_name])) minRow = row;
            if (v > parseFloat(maxRow[primary.reference_name])) maxRow = row;
        });

        let movingAvg: string | null = null;
        if (kpiVariance.includeKpiVariance == 'Y') {
            const lastKpiRow = kpiData.find((row) => String(row[xAxisField]) === String(last[xAxisField]));
            if (lastKpiRow?.moving_avg != null) {
                //@ts-ignore
                movingAvg = formatValueFull(lastKpiRow.moving_avg as number, dm);
            }
        }

        const { varianceTarget } = kpiVariance;
        const varianceTargetLabel = varianceTarget
            ? varianceTarget.aggregation_function && varianceTarget.aggregation_function !== 'None'
                ? `${varianceTarget.aggregation_function} of ${varianceTarget.column_name}`
                : varianceTarget.column_name
            : '';

        return {
            currValue: getFormattedValue(last, primary.reference_name),
            additionalMeasureValues,
            lastMeasurementTimeFormatted: getFormattedValue(last, xAxisField),
            metricTileDisplayPctVariance: variance,
            metricTileDisplayVarianceMode: variance != null ? varianceMode : undefined,
            metricTileDisplayVarianceFormatted: varianceFormatted,
            metricTileDisplayVarianceTargetValueFormatted: varianceTargetValueFormatted,
            pctVarianceText:
                variance != null
                    ? kpiVariance.varianceCalcMethod === 'target comparison'
                        ? t('kpi_chart.summary_pct_change_suffix_target', { column: varianceTargetLabel })
                        : t('kpi_chart.summary_pct_change_suffix')
                    : '',
            movingAverageValue: movingAvg ?? '',
            metricMovingAverageInterval: movingAvg != null ? getKpiChartVariancePeriodLabel(kpiVariance, t) : '',
            minValue: getFormattedValue(minRow, primary.reference_name),
            minReachedOn: getFormattedValue(minRow, xAxisField),
            maxValue: getFormattedValue(maxRow, primary.reference_name),
            maxReachedOn: getFormattedValue(maxRow, xAxisField),
            intervalUnit: '',
            measurementIntervalName: '',
            metricDescr: '',
            metricElementId: elementId,
            pureMetricName: getKpiChartMeasureFieldDisplayLabel(primary),
            segmentValueDisplayName: '',
            segmentValueId: 0,
        };
    }, [data, kpiData, measureFields, xAxisField, kpiVariance, kpiVarianceResult, displayMasks, elementId, t]);
}
